import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Page,
  Layout,
  IndexTable,
  Button,
  Text,
  Avatar,
  InlineStack,
  Box,
  BlockStack,
  Icon
} from '@shopify/polaris';
import { useNavigate } from 'react-router-dom';
import useAxiosClient from '../axiosClient';
import {
  PlusIcon, PersonIcon, WorkIcon, MoneyIcon, ClockIcon,
  CashDollarIcon, TaxIcon, WalletIcon, ReceiptIcon, SettingsIcon,
} from '@shopify/polaris-icons';
import { ColumnHeading, CardHeader, SURFACE } from '../ui/CardSurface';

const Payroll = () => {
  const { t } = useTranslation();
  const axiosClient = useAxiosClient();
  const navigate = useNavigate();
  const [staffs, setStaffs] = useState([]);
  const [loading, setLoading] = useState(true);
  const { currencyData = {} } = window.appGlobals || {};
  const getInitials = (firstName, lastName) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  };

  useEffect(() => {
    fetchStaffs();
  }, []);

  const fetchStaffs = async () => {
    try {
      const response = await axiosClient.get('/getStaffWithPayroll');
      if (response.data.success && Array.isArray(response.data.staff)) {
        setStaffs(response.data.staff);
      } else {
        setStaffs([]);
      }
    } catch (error) {
      setStaffs([]);
    }
  };

  const handleCreatePayroll = (staff) => {
    navigate(`/Payroll/payrollCreate/${staff.id}`);
  };

  // Helper function to safely get currency symbol
  const getCurrencySymbol = (currency) => {
    return currencyData && currency ? currencyData[currency]?.symbol || '' : '';
  };

  const formatCurrency = (amount, currency) => {
    const symbol = getCurrencySymbol(currency);
    return `${symbol}${Number(amount || 0).toFixed(2)}`;
  };

  const convertToHMS = (decimalHours) => {
    const totalSeconds = Math.floor(decimalHours * 3600);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return `${hours}h ${minutes}m ${seconds}s`;
  };

  const formatHours = (hours) => {
    return convertToHMS(Number(hours));
  };

  // Tax columns only appear once a merchant actually configures tax on at least
  // one staff member, so the table is unchanged for everyone who does not use it.
  const showTaxColumns = staffs.some((s) => s && s.tax_type);

  return (
    <Page fullWidth>
      {/* Page header - title and the product wordmark, as on Staff. */}
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <Text as="h1" variant="headingLg">{t("payroll_management_title")}</Text>
          <div style={{ marginTop: '2px' }}>
            <Text as="p" variant="bodySm" tone="subdued">{t("payroll_subtitle")}</Text>
          </div>
        </div>
      </div>
      <Layout>
        <Layout.Section>
          <div style={{ ...SURFACE, overflow: 'visible' }}>
            <CardHeader
              icon={CashDollarIcon}
              tone="success"
              title={t("payroll_management_title")}
              context={!loading ? t("reports_staff_count", { count: staffs.length }) : null}
            />

            {staffs.length === 0 ? (
              <Box padding="800">
                <BlockStack align="center" inlineAlign="center" gap="400">
                  <Box
                    background="bg-surface-secondary"
                    borderRadius="full"
                    padding="400"
                  >
                    <Icon source={PersonIcon} tone="subdued" />
                  </Box>
                  <BlockStack align="center" inlineAlign="center" gap="200">
                    <Text variant="headingMd" as="h3">
                      {t("no_payroll_records_yet")}
                    </Text>
                    <Text tone="subdued" alignment="center">
                      {t("create_first_payroll_description")}
                    </Text>
                  </BlockStack>
                </BlockStack>
              </Box>
            ) : (
            <IndexTable
              resourceName={{ singular: 'staff', plural: 'staff' }}
              itemCount={staffs.length}
              headings={[
                { id: 'name', title: <ColumnHeading icon={PersonIcon}>{t("name_column")}</ColumnHeading> },
                { id: 'payment_type', title: <ColumnHeading icon={WorkIcon}>{t("payment_type_column")}</ColumnHeading> },
                { id: 'rate', title: <ColumnHeading icon={MoneyIcon}>{t("rate_column")}</ColumnHeading> },
                { id: 'total_hours', title: <ColumnHeading icon={ClockIcon}>{t("total_hours_column")}</ColumnHeading> },
                { id: 'working_hours', title: <ColumnHeading icon={ClockIcon}>{t("working_hours_column")}</ColumnHeading> },
                { id: 'break_time', title: <ColumnHeading icon={ClockIcon}>{t("total_break_time_column")}</ColumnHeading> },
                { id: 'total_earnings', title: <ColumnHeading icon={CashDollarIcon}>{t("total_earnings_column")}</ColumnHeading> },
                ...(showTaxColumns
                  ? [
                      { id: 'tax_deduction', title: <ColumnHeading icon={TaxIcon}>{t("tax_deduction")}</ColumnHeading> },
                      { id: 'net_pay', title: <ColumnHeading icon={WalletIcon}>{t("net_pay")}</ColumnHeading> },
                    ]
                  : []),
                { id: 'total_paid', title: <ColumnHeading icon={ReceiptIcon}>{t("total_paid_column")}</ColumnHeading> },
                { id: 'remaining', title: <ColumnHeading icon={MoneyIcon}>{t("remaining_column")}</ColumnHeading> },
                { id: 'action', title: <ColumnHeading icon={SettingsIcon}>{t("action_column")}</ColumnHeading> }
              ]}
              selectable={false}
            >
              {staffs.map((staff) => staff ? (
                <IndexTable.Row key={staff.id} id={staff.id} onClick={() => navigate(`/StaffManagement/staffDetails/${staff.id}`)}>
                  <IndexTable.Cell>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <Avatar
                        name={`${staff.first_name || ''} ${staff.last_name || ''}`}
                        initials={getInitials(staff.first_name, staff.last_name)}
                      />
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <Text variant="bodyMd" fontWeight="bold">
                          {`${staff.first_name || ''} ${staff.last_name || ''}`}
                        </Text>
                        {staff.email && (
                          <Text variant="bodySm" color="subdued">
                            {staff.email}
                          </Text>
                        )}
                      </div>
                    </div>
                  </IndexTable.Cell>
                  <IndexTable.Cell>{staff.payroll_type || 'N/A'}</IndexTable.Cell>
                  <IndexTable.Cell>{formatCurrency(staff.salary_amount, staff.currency)}</IndexTable.Cell>
                  <IndexTable.Cell>{staff.totalHours || '0h 0m 0s'}</IndexTable.Cell>
                  <IndexTable.Cell>{staff.working_hours || '0h 0m 0s'}</IndexTable.Cell>
                  <IndexTable.Cell>{staff.total_break_time || '0h 0m 0s'}</IndexTable.Cell>
                  <IndexTable.Cell>{formatCurrency(staff.totalEarnings, staff.currency)}</IndexTable.Cell>
                  {showTaxColumns && (
                    <IndexTable.Cell>{formatCurrency(staff.tax_deduction || 0, staff.currency)}</IndexTable.Cell>
                  )}
                  {showTaxColumns && (
                    <IndexTable.Cell>
                      <Text variant="bodyMd" fontWeight={staff.tax_type ? 'bold' : 'regular'}>
                        {formatCurrency(
                          staff.net_earnings ?? staff.totalEarnings,
                          staff.currency
                        )}
                      </Text>
                    </IndexTable.Cell>
                  )}
                  <IndexTable.Cell>{formatCurrency(staff.totalPaid, staff.currency)}</IndexTable.Cell>
                  <IndexTable.Cell>{formatCurrency(staff.totalRemaining, staff.currency)}</IndexTable.Cell>
                  <IndexTable.Cell>
                  <Button
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent row click event
                          handleCreatePayroll(staff);
                        }}
                        size="slim"
                        variant="primary"
                        icon={PlusIcon}
                      >
                        {t("create_payroll_button")}
                      </Button>
                  </IndexTable.Cell>
                </IndexTable.Row>
              ) : null)}
            </IndexTable>
            )}
          </div>
        </Layout.Section>
      </Layout>
    </Page>
  );
};

export default Payroll;
